import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { getVerifiedSession } from '@/lib/sessionGuard';
import { createScopedLogger } from '@/utils/logger.js';
import { isCouncilBadge, isAdminBadge } from '@/constants/authorityBadges';

/**
 * LotAccessGuard - Enforces lot-based access control for management dashboard
 *
 * Access Rules:
 * - Admin (admin-access badge) → Access all lots
 * - Council (council-member badge) → Access all lots
 * - KOT (kot badge) → Access only assigned lots via roster_assignments
 * - Valet (valet-certified badge) → Redirect to /valet
 * - No badges → Redirect to /auth
 *
 * Props:
 *   - children: Components to render if access granted
 *   - requiredLevel: 'kot' | 'council' | 'admin' (default: 'kot')
 *   - lotId: (optional) Specific lot to check access for
 */
const log = createScopedLogger('LotAccessGuard');

// Distinguish a genuinely-absent record from one hidden by a degraded identity.
// The valets self-read RLS predicate is (auth_user_id = auth.uid() OR user_id = auth.uid()).
// During an infra incident the JWT can go stale / mid-refresh, so REST queries run as an
// unauthenticated identity (auth.uid() -> null): RLS filters the user's OWN rows and the
// query returns { data: null, error: null } — byte-for-byte identical to "no row exists".
// Never conclude "no valet -> join CTA" without first proving the session is actually healthy.
const isSessionHealthy = async () => {
  try {
    const { data: { session }, error } = await Promise.race([
      supabase.auth.getSession(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('getSession timed out')), 4000)),
    ]);
    if (error || !session) return false;
    // Expired access token with a stalled refresh = degraded, not "logged out on purpose".
    if (session.expires_at && session.expires_at * 1000 <= Date.now()) return false;
    return true;
  } catch {
    // getSession() hanging (refresh wedged during an outage) is itself an unhealthy signal.
    return false;
  }
};

const LotAccessGuard = ({ children, requiredLevel = 'kot', lotId = null }) => {
  const { user, loading, authReady, rolesDegraded } = useAuth();
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [authWaitTime, setAuthWaitTime] = useState(0);
  // Infra-degraded state: we couldn't verify access due to a transient/connectivity
  // problem (not a real permissions failure). Surface a retry, never a wrong dead-end.
  const [degraded, setDegraded] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  // If badge check completes and grants access, clear checking state immediately.
  // No emergency bypass — the check itself handles all outcomes.

  // Safety timeout - if auth takes too long, redirect instead of infinite spinner
  useEffect(() => {
    if (!authReady && loading) {
      const interval = setInterval(() => {
        setAuthWaitTime(prev => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [authReady, loading]);

  useEffect(() => {
    const checkAccess = async () => {
      // Wait for auth to finish loading
      if (loading) {
        return;
      }

      // Wait for authReady - OAuth processing complete and client unblocked
      if (!authReady) {
        log.debug?.('Waiting for auth to be ready...');
        // After 10 seconds, redirect to auth instead of spinning forever
        if (authWaitTime >= 10) {
          log.error?.('Auth timeout - redirecting to /auth');
          navigate('/auth', { replace: true });
          setIsChecking(false);
        }
        return;
      }

      if (!user) {
        setIsChecking(false);
        return;
      }

      try {
        // Step 0: Client portal users NEVER access ops dashboard
        // Check auth metadata first — this is set during client portal signup
        if (user.user_metadata?.portal_access === true || user.user_metadata?.role === 'client') {
          log.info?.('Client portal user detected via metadata, redirecting to portal', { userId: user.id });
          navigate('/portal/dashboard', { replace: true });
          setIsChecking(false);
          setIsAuthorized(false);
          return;
        }

        // Use the main unified supabase client - auth is complete at this point
        // Step 1: Find the valet record for this auth user
        // Check both auth_user_id (new onboarding) and user_id (legacy)
        const userId = user.id;
        // No valets read without a verified session for this user (a stale token sends
        // the anon key). Surface the retry state; the effect re-runs when AuthContext
        // updates user/authReady on the next auth event. anon-valets review 2026-09-30.
        if (!(await getVerifiedSession(supabase.auth, userId))) {
          log.warn?.('No verified session yet; not querying valets, surfacing retry', { userId });
          setDegraded(true);
          setIsChecking(false);
          setIsAuthorized(false);
          return;
        }
        let { data: valet, error: valetError } = await supabase
          .from('valets')
          .select('id')
          .or(`auth_user_id.eq.${userId},user_id.eq.${userId}`)
          .maybeSingle();

        if (valetError) {
          // A hard query error (401/5xx) during a lookup is an infra symptom, not a
          // permissions verdict. Surface a retry instead of the bare Access Denied screen.
          log.error?.('Failed to fetch valet record — surfacing retry, not a verdict', valetError);
          setDegraded(true);
          setIsChecking(false);
          setIsAuthorized(false);
          return;
        }

        if (!valet) {
          // Empty WITHOUT an error is ambiguous: it means EITHER "genuinely no valet row"
          // OR "RLS filtered the row because our identity is degraded" (stale/refreshing JWT
          // during an incident -> auth.uid() null -> 0 rows, no error). Never misroute a real
          // valet to the join CTA on an infra blip: verify the session, then retry once.
          // rolesDegraded (from AuthContext) is an authoritative "auth layer is degraded
          // right now" signal — if its own role resolution just timed out, an empty valet
          // read here is almost certainly RLS-filtered, not genuinely absent. Treat as
          // unhealthy without even probing the session.
          const healthy = !rolesDegraded && (await isSessionHealthy());
          if (!healthy) {
            log.warn?.('Valet lookup empty under unhealthy session — surfacing retry, not /auth', { userId: user.id });
            setDegraded(true);
            setIsChecking(false);
            setIsAuthorized(false);
            return;
          }

          // Session looks healthy — retry once with a short backoff to rule out a transient
          // RLS/replica glitch before treating the row as genuinely absent.
          await new Promise((r) => setTimeout(r, 800));
          const { data: valetRetry, error: retryError } = await supabase
            .from('valets')
            .select('id')
            .or(`auth_user_id.eq.${userId},user_id.eq.${userId}`)
            .maybeSingle();

          if (retryError) {
            log.error?.('Valet lookup retry failed — surfacing retry, not /auth', retryError);
            setDegraded(true);
            setIsChecking(false);
            setIsAuthorized(false);
            return;
          }

          if (!valetRetry) {
            // Confirmed absent under a proven-healthy session — genuinely not a valet.
            log.info?.('No valet record found for user (confirmed, healthy session)', { userId: user.id });
            setIsChecking(false);
            setIsAuthorized(false);
            navigate('/auth', { replace: true });
            return;
          }

          valet = valetRetry; // retry recovered the row — proceed with normal badge checks
        }

        // Step 2: Fetch user badges from badge_assignments using valet_id
        const { data: badges, error: badgeError } = await supabase
          .from('badge_assignments')
          .select('badge_type')
          .eq('valet_id', valet.id)
          .eq('is_active', true);

        if (badgeError) {
          // Same infra-symptom class as the valet lookup: a failed badge read under a
          // degraded identity must not read as "no permissions". Surface a retry.
          log.error?.('Failed to fetch badges — surfacing retry, not a verdict', badgeError);
          setDegraded(true);
          setIsChecking(false);
          setIsAuthorized(false);
          return;
        }

        const badgeTypes = badges.map(b => b.badge_type).filter(Boolean);
        log.debug?.('User badges', { badgeTypes });

        // Step 2b: Canonical location-scoped leadership check (badge OR captain-roster OR account_type).
        // Codex's 2026-07-08 scope fix moved captains off global captain badges onto location_rosters;
        // the badge-only gate stopped seeing them and bounced them to /valet. is_kot_leader() reads both.
        let isRosterLeader = false;
        try {
          const { data: leaderFlag, error: leaderError } = await supabase
            .rpc('is_kot_leader', { check_user_id: userId, p_location_id: lotId || null });
          if (leaderError) {
            log.warn?.('is_kot_leader check failed (non-fatal)', leaderError.message);
          } else {
            isRosterLeader = leaderFlag === true;
          }
        } catch (leaderErr) {
          log.warn?.('is_kot_leader threw (non-fatal)', leaderErr?.message);
        }

        // Helper functions for badge checking
        const hasBadge = (type) => badgeTypes.includes(type);
        const hasBadgePrefix = (prefix) => badgeTypes.some(
          (type) => type === prefix || type.startsWith(`${prefix}-`)
        );

        // Step 2: Block valets and clients from accessing KOT/management areas
        // Only admin, council, and KOT can access odv-truest
        const isValet = hasBadge('valet-certified') || hasBadgePrefix('valet');
        const isClient = hasBadge('client');
        const hasKOTAccess = hasBadge('kot') || hasBadgePrefix('kot') || isRosterLeader;
        const hasCaptainAccess = hasBadge('captain') || hasBadgePrefix('captain') || isRosterLeader;
        // Both from the ONE definition in constants/authorityBadges.js. This guard
        // fetches badges itself (it runs before useBadges is usable), but it must
        // not re-derive WHICH badges count — that divergence is what let the lot
        // guard say yes while the screen inside said no.
        const hasCouncilAccess = isCouncilBadge(badgeTypes);
        const hasAdminAccess = isAdminBadge(badgeTypes);

        if ((isValet || isClient) && !hasKOTAccess && !hasCaptainAccess && !hasCouncilAccess && !hasAdminAccess) {
          log.info?.('User is valet/client only, redirecting', { userId: user.id });
          if (isValet) {
            navigate('/valet', { replace: true });
          } else {
            navigate('/portal', { replace: true });
          }
          setIsChecking(false);
          setIsAuthorized(false);
          return;
        }

        // Step 3: Check role hierarchy
        // Admin can access everything
        if (hasAdminAccess) {
          log.debug?.('User has admin-access badge');
        }

        // Council can access everything (but not admin-only features)
        if (hasCouncilAccess) {
          log.debug?.('User has council-member badge');
        }

        // KOT needs lot-specific checks
        if (hasKOTAccess) {
          log.debug?.('User has kot badge');
        }

        // Step 3: Check required level
        let isAccessGranted = false;

        if (requiredLevel === 'admin' && hasAdminAccess) {
          isAccessGranted = true;
        } else if (requiredLevel === 'council' && (hasAdminAccess || hasCouncilAccess)) {
          isAccessGranted = true;
        } else if (requiredLevel === 'kot' && (hasAdminAccess || hasCouncilAccess || hasKOTAccess || hasCaptainAccess)) {
          isAccessGranted = true;
        }

        if (!isAccessGranted) {
          log.info?.('User missing required badge', { requiredLevel });
          setIsChecking(false);
          setIsAuthorized(false);
          navigate('/auth', { replace: true });
          return;
        }

        // All checks passed
        log.debug?.('User authorized for access');
        setIsAuthorized(true);
      } catch (error) {
        log.error?.('Access check failed', error);
        setIsAuthorized(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, authReady, authWaitTime, lotId, requiredLevel, retryNonce, rolesDegraded]); // navigate is stable, don't include it

  // Best-effort heal + re-run the access check. Actively try a token refresh (the usual
  // cause of the degraded state) before re-evaluating, then bump the nonce to re-fire the effect.
  const handleRetry = async () => {
    setDegraded(false);
    setIsChecking(true);
    setIsAuthorized(false);
    setAuthWaitTime(0);
    try {
      await Promise.race([
        supabase.auth.refreshSession(),
        new Promise((resolve) => setTimeout(resolve, 4000)),
      ]);
    } catch {
      // best-effort — the re-run re-evaluates session health regardless
    }
    setRetryNonce((n) => n + 1);
  };

  // Still checking authorization
  if (isChecking || loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vegas-gold mx-auto mb-4"></div>
          <p className="text-gray-400">Verifying access...</p>
          {authWaitTime >= 5 && (
            <p className="text-yellow-500 text-sm mt-2">
              Taking longer than usual... {authWaitTime >= 8 ? 'Redirecting soon...' : ''}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Infra-degraded — we could not verify access due to a connectivity/session problem,
  // NOT a permissions denial. No-blockades doctrine: offer a retry, never a wrong dead-end.
  if (degraded) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-center max-w-sm px-6">
          <h1 className="text-2xl font-bold text-yellow-500 mb-3">Connection issue</h1>
          <p className="text-gray-400 mb-8">
            We couldn't verify your access just now. This looks like a temporary connection
            problem, not a permissions issue. Please try again.
          </p>
          <button
            onClick={handleRetry}
            className="px-6 py-2 bg-vegas-gold text-black rounded-lg hover:bg-yellow-500"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Not authorized
  if (!isAuthorized) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-500 mb-4">Access Denied</h1>
          <p className="text-gray-400 mb-8">You don't have permission to access this area.</p>
          <button
            onClick={() => navigate('/operations/assignments', { replace: true })}
            className="px-6 py-2 bg-vegas-gold text-black rounded-lg hover:bg-yellow-500"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Authorized - render children
  return <>{children}</>;
};

export default LotAccessGuard;
